import { PII_MASK_CLASS } from "@/lib/analytics";
import type { HttpTypes } from "@medusajs/types";
import { RadioGroup } from "@medusajs/ui";
import { memo } from "react";

interface AddressChoiceListProps {
  /** The saved addresses to choose between. */
  addresses: HttpTypes.StoreCustomerAddress[];
  /** The id of the chosen address. */
  value: string;
  onValueChange: (addressId: string) => void;
  /** Disable the whole list, e.g. while the choice is being saved. */
  disabled?: boolean;
}

/**
 * One line for the street, locality and country, in the order a postal address
 * is written (city, then state and postcode). `AddressCard` and
 * `AddressDropdown` each print an address differently on purpose - the card
 * leads the locality with the postcode, the dropdown styles street and locality
 * as separate spans and omits the country - so this is not shared with them.
 */
function formatAddressLine(address: HttpTypes.StoreCustomerAddress): string {
  return [
    address.address_1,
    address.address_2,
    address.city,
    [address.province, address.postal_code].filter(Boolean).join(" "),
    address.country_code?.toUpperCase()
  ]
    .filter(Boolean)
    .join(", ");
}

/**
 * The title of a choice: what the salon called the address, else who it is
 * for, else the street - something they will recognise.
 */
function formatChoiceTitle(address: HttpTypes.StoreCustomerAddress): string {
  const fullName = [address.first_name, address.last_name]
    .filter(Boolean)
    .join(" ");
  return address.address_name || address.company || fullName || "Saved address";
}

/**
 * Pick one of the salon's saved addresses.
 *
 * `AddressCard` is the account page's management surface (edit, delete, set
 * default) and `AddressDropdown` is checkout's compact select; neither fits a
 * step whose whole job is "which of these?". This is the design system's own
 * radio choice box, one per address, with the address printed under its name.
 *
 * Every address is printed as text, which `maskAllInputs` does not reach, so the
 * list carries `PII_MASK_CLASS` on its root (ADR-0006). Nothing here portals out
 * of it.
 */
export const AddressChoiceList = memo(function AddressChoiceList({
  addresses,
  value,
  onValueChange,
  disabled = false
}: AddressChoiceListProps) {
  return (
    <RadioGroup
      value={value}
      onValueChange={onValueChange}
      disabled={disabled}
      aria-label="Saved addresses"
      className={PII_MASK_CLASS}
    >
      {addresses.map((address) => (
        <RadioGroup.ChoiceBox
          key={address.id}
          value={address.id}
          label={formatChoiceTitle(address)}
          description={formatAddressLine(address)}
        />
      ))}
    </RadioGroup>
  );
});
